import { useEffect, useMemo, useRef, useState } from 'react'
import { displayTitle } from '../../lib/displayName'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalAlert,
  MinimalBadge,
  MinimalButton,
  MinimalCard,
  MinimalEmptyState,
  MinimalTooltip,
} from '@ovasabi/ui-minimal'
import { ChowModal, Money, PageHeader, Screen, SearchField, Stepper } from '../../components/chow'
import {
  BreakfastIcon,
  CardIcon,
  CopyIcon,
  DinnerIcon,
  InfoIcon,
  LunchIcon,
  PlateIcon,
  PlusIcon,
  SparkIcon,
} from '../../components/icons'
import { useMarketplaceOrderSnapshot } from '../../generated/prototypeRuntime'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { confirmedUpsert, echoUpsert, enumShort, type StoreRecord } from './chefShared'
import { ChowThumb } from './ChowThumb'
import { useChefKitchens, useChefOpenMap, useUiDishes, type UiDish } from './projections'
import { useOrderItemNamesState } from './orderDisplay'
import {
  FIRM_STATUSES,
  LARGE_HOLD_MINOR,
  dayKey,
  addPlanToCart,
  dayTotalsByKey,
  depositFor,
  holdCutoffMs,
  isoWeekday,
  naira,
  parseKey,
  planDish,
  planPreview,
  planWindow,
  scheduledAtFor,
  type PlannedMeal,
} from './mealPlanning'
import { useChooseChow, usePageContext } from './ChooseChowContext'
import { removeReservationBasketIds, reservationScheduledAt, useReservationBasketIds } from './reservationBasket'

/**
 * Meal Calendar — the customer's forward planner (Phase 1 of the reservation
 * model). A planned meal is a HOLD: an unpaid order with a future
 * `scheduled_at`. A normal order pays immediately, so an unpaid future-dated
 * order is, by definition, a reservation. The kitchen sees the same rows on its
 * prep timeline. The cart pays selected reservations together; a hold
 * past its confirm-by cutoff is treated as expired and never reaches the chef's
 * firm counts.
 */


interface Hold {
  id: string
  ids?: string[]
  dishId?: string
  dishName: string
  chefName?: string
  dishImage?: string
  quantity: number
  totalMinor: number
  amountPaidMinor: number
  depositMinor: number
  pickup: boolean
  status: string
  slot: MealSlotKey
}
interface DayPlan {
  key: string
  date: Date
  weekday: string
  label: string
  held: Hold[]
  firm: Hold[]
  /** confirm-by moment; a held meal past this is expired. */
  cutoff: Date
  expired: boolean
  /** party-sized day (total >= LARGE_HOLD_MINOR): earlier confirm-by. */
  largeHold: boolean
  totalMinor: number
}

const MEAL_SLOTS = [
  { key: 'breakfast', label: 'Breakfast', short: 'AM', Icon: BreakfastIcon },
  { key: 'lunch', label: 'Lunch', short: 'Noon', Icon: LunchIcon },
  { key: 'dinner', label: 'Dinner', short: 'PM', Icon: DinnerIcon },
] as const

type MealSlotKey = (typeof MEAL_SLOTS)[number]['key']

const slotForDish = (dish: Pick<UiDish, 'category' | 'name'> | undefined, index: number): MealSlotKey => {
  if (dish?.category === 'breakfast') return 'breakfast'
  const name = dish?.name.toLowerCase() ?? ''
  if (/\b(breakfast|akara|pap|tea|coffee|pancake|toast)\b/.test(name)) return 'breakfast'
  return index % 2 === 0 ? 'lunch' : 'dinner'
}

const isGenericMealName = (value: string): boolean => {
  const norm = value.trim().toLowerCase()
  return !norm || norm === 'meal' || norm === 'order details' || norm === 'planned dish'
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 350px), 1fr));
  gap: ${v.space.sm};
  align-items: start;

  @media (max-width: 599px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`
const DayCard = styled(MinimalCard)`
  display: flex;
  flex-direction: column;
  min-width: 0;
  > div { display: flex; flex-direction: column; gap: ${v.space.xs}; }
`
const PlanBody = styled.div`
  display: grid;
  gap: ${v.space.xs};
`
const PaymentNote = styled.p`
  margin: 0;
  color: ${v.color.textSecondary};
  font-size: ${v.typography.captionSize};
  line-height: 1.5;
`
/** Says what the basket action did, including what it left out. */
const BasketNote = styled.p`
  margin: 0;
  color: ${v.color.textTertiary};
  font-size: ${v.typography.captionSize};
  line-height: 1.5;
`
const SummaryToggle = styled(MinimalButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid ${v.color.borderSubtle};
  border-radius: ${v.radius.pill};
  background: ${v.color.bgSurface};
  color: ${v.color.textSecondary};
  font: inherit;
  font-size: ${v.typography.captionSize};
  font-weight: 800;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${v.color.brand};
    outline-offset: 2px;
  }
`
/* Everything above the day grid is ONE block with its own tight rhythm. The
   shell puts a large gap between top-level children, so intro / suggest /
   controls must not be siblings of the grid or they drift far apart. */
/* ── Plan toolbar ─────────────────────────────────────────────────────────
   Three stacked bands of prose — an intro paragraph, a tinted suggestion
   banner, and an auto-pay row with its own explanation — took more vertical
   space above the week than the week itself, and said the same thing on every
   visit to someone who had read it once. They collapse into one control strip:
   the week switcher on the left, icon actions on the right, and the sentences
   moved into tooltips where they are available on demand instead of always. */
const PlanHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${v.space.xs};
  flex-wrap: wrap;
`
const WeekNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${v.space.xs};
  flex-wrap: wrap;
`
const WeekRange = styled.span`
  font-size: ${v.typography.captionSize};
  color: ${v.color.textTertiary};
`
const PlanControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`
/* An icon action carries its meaning in a tooltip and its accessible name in
   aria-label, so nothing is lost for a screen reader or a keyboard. `$on` is
   the state an action can be left in (auto-pay), not a hover. */
const ToolButton = styled(MinimalButton)<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0 12px;
  gap: 6px;
  border-radius: ${v.radius.pill};
  cursor: pointer;
  border: 1px solid
    ${({ $on }) => ($on ? 'transparent' : v.color.borderSubtle)};
  background: ${({ $on }) => ($on ? v.color.brand : v.color.bgSurface)};
  color: ${({ $on }) => ($on ? v.color.textInverse : v.color.textSecondary)};
  transition: background 140ms ease, color 140ms ease, border-color 140ms ease;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${v.color.brand};
    outline-offset: 2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      border-color: ${v.color.brand};
      color: ${({ $on }) => ($on ? v.color.textInverse : v.color.brand)};
    }
  }
`
const DayTitle = styled.h3`
  margin: 0;
  font-family: ${v.typography.displayFamily};
  font-weight: ${v.typography.weightBold};
  font-size: ${v.typography.bodySize};
  line-height: 1.25;
  white-space: nowrap;
`
const DaySub = styled.span`
  font-size: ${v.typography.captionSize};
  color: ${v.color.textTertiary};
`
const MealName = styled.span`
  min-width: 0;
  font-weight: ${v.typography.weightSemibold};
  color: ${v.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
  word-break: break-word;
`
const DayFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 6px;
  margin-top: ${v.space.xs};
  padding-top: ${v.space.xs};
  flex-wrap: wrap;
  > button {
    flex: 1 1 116px;
    min-height: 44px;
    padding-inline: 10px;
    font-size: ${v.typography.metaSize};
    font-weight: 700;
    border-radius: ${v.radius.pill};
    white-space: normal;
    line-height: 1.15;
  }
`
const QtyRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${v.space.xs};
  margin-top: ${v.space.xs};
  font-size: ${v.typography.captionSize};
  color: ${v.color.textSecondary};
`
const SearchHint = styled.p`
  margin: 0 0 ${v.space['2xs']} 0;
  font-size: ${v.typography.captionSize};
  color: ${v.color.textSecondary};
  font-weight: ${v.typography.weightMedium};
`
const SlotHint = styled.div`
  margin-top: ${v.space.xs};
  padding: 8px 12px;
  background: ${v.color.brandSoft};
  color: ${v.color.brand};
  border-radius: ${v.radius.md};
  font-size: ${v.typography.captionSize};
  font-weight: ${v.typography.weightMedium};
`
/* The search sits below the added-meal note, so it carries a top margin. */
const PickerSearch = styled.div`
  margin-top: ${v.space.xs};
`
const PickList = styled.div`
  display: grid;
  gap: ${v.space['2xs']};
  max-height: 52vh;
  min-height: 320px;
  overflow-y: auto;
  margin-top: ${v.space.xs};
  contain: layout;
  scroll-behavior: smooth;
`
const PickRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: 1px solid ${v.color.borderSubtle};
  border-radius: ${v.radius.md};
  background: ${v.color.bgSurface};
  cursor: pointer;
  font: inherit;
  transition: border-color 140ms ease, background-color 140ms ease;
  &:hover {
    border-color: ${v.color.brand};
    background: ${v.color.bgSurfaceHover};
  }
`
const PickThumb = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: ${v.radius.sm};
  overflow: hidden;
  & > * {
    width: 100%;
    height: 100%;
  }
`
const MealThumb = styled(PickThumb)`
  width: 34px;
  height: 34px;
  flex-basis: 34px;
`
const PickMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 2px;
`

/* A running tally of the day inside the picker: what you have already chosen
   stays visible while you choose the next thing, which is what makes planning
   several meals for one day feel like one task instead of a repeated one. */
const PlannedSoFar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  border-radius: ${v.radius.md};
  background: ${v.color.bgSurfaceAlt};
  border: 1px solid ${v.color.borderSubtle};
  color: ${v.color.textSecondary};
  font-size: ${v.typography.captionSize};
  line-height: 1.4;
`
const AddedNote = styled.div`
  display: flex;
  align-items: center;
  gap: ${v.space.xs};
  margin: 0;
  padding: ${v.space.xs};
  border-radius: ${v.radius.md};
  background: ${v.color.successSoft};
  border: 1px solid color-mix(in srgb, ${v.color.success} 22%, transparent);
  color: ${v.color.success};
  font-size: ${v.typography.captionSize};
  font-weight: ${v.typography.weightSemibold};
`
const AddedTick = styled.span`
  font-size: ${v.typography.bodySize};
  font-weight: ${v.typography.weightBold};
`

const Review = styled.div`
  display: grid;
  gap: ${v.space.xs};
  margin-top: ${v.space.xs};
`
const ReviewTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${v.space.xs};
`
const ReviewThumb = styled.div`
  width: 88px;
  height: 88px;
  flex: 0 0 88px;
  border-radius: ${v.radius.md};
  overflow: hidden;
  & > * {
    width: 100%;
    height: 100%;
  }
`
const ReviewTitle = styled.div`
  display: grid;
  gap: ${v.space["2xs"]};
  min-width: 0;
`
const ReviewName = styled.span`
  font-weight: ${v.typography.weightSemibold};
  font-size: ${v.typography.bodySize};
  color: ${v.color.textPrimary};
`
/* The delivery promise, set apart from the dish copy: it is the answer to
   "when does this actually reach me, and from where", which is the question a
   forward-planned meal raises and a price does not answer. */
const DeliveryNote = styled.div`
  display: grid;
  gap: 2px;
  padding: ${v.space.xs};
  border-radius: ${v.radius.md};
  background: ${v.color.bgSurfaceAlt};
  font-size: ${v.typography.captionSize};
  color: ${v.color.textSecondary};
  line-height: ${v.typography.lineHeightBody};
`
const DeliveryWarn = styled.span`
  color: ${v.color.warning};
`
const ReviewActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${v.space.xs};
  flex-wrap: wrap;
`

/* ── Day card, in the Chefs card language ────────────────────────────────
   A warm date tile (mirroring the chef avatar tile), a title row with a status
   chip, and meals rendered as mini cards with a thumbnail + badge + price —
   the same visual grammar as ChefRow / DishRow. */
const DayTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
const DateTile = styled.div<{ $today?: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${({ $today }) => ($today ? 'transparent' : v.color.borderSubtle)};
  background: ${({ $today }) => ($today ? v.color.brand : v.color.brandSoft)};
  color: ${({ $today }) => ($today ? v.color.textInverse : v.color.brand)};
`
const DateNum = styled.span`
  font-family: ${v.typography.displayFamily};
  font-weight: ${v.typography.weightBold};
  font-size: ${v.typography.bodySize};
  line-height: 1;
`
const DateMon = styled.span`
  font-size: 0.53125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 1px;
  opacity: 0.85;
`
const DayTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
`
/* The weekday and its caption share one shrinkable column beside the date tile. */
const DayHeading = styled.div`
  flex: 1;
  min-width: 0;
`
/* A party-sized day commits sooner, so its earlier confirm-by is stated in
   warning colour instead of the neutral caption below. */
const BigOrderNote = styled(DaySub)`
  display: block;
  margin-top: ${v.space.xs};
  color: ${v.color.warning};
`
const MealCard = styled.div<{ $muted?: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  min-height: 48px;
  padding: ${v.space.xs};
  border: 1px solid ${v.color.borderSubtle};
  border-radius: 10px;
  background: ${v.color.bgSurfaceAlt};
  opacity: ${({ $muted }) => ($muted ? 0.62 : 1)};
`
const MealSlot = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: stretch;
  gap: 6px;
`
const MealSlotLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 24px;
  color: ${v.color.textTertiary};
  font-size: ${v.typography.metaSize};
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  overflow: hidden;

  > svg {
    color: ${v.color.brand};
    flex-shrink: 0;
  }

  > span {
    min-width: 0;
    overflow: visible;
  }
`
const EmptyMealSlot = styled(MinimalButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 48px;
  padding: 7px 10px;
  border: 1.5px dashed ${v.color.borderSubtle};
  border-radius: 10px;
  background: ${v.color.bgSurface};
  color: ${v.color.textSecondary};
  font-size: ${v.typography.captionSize};
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: ${v.color.brand};
    color: ${v.color.brand};
    background: ${v.color.brandSoft};
  }
`
const MealMain = styled.div`
  display: grid;
  gap: ${v.space["2xs"]};
  min-width: 0;
`
const MealTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
  > :last-child { flex-shrink: 0; }
`
const MealChef = styled.span`
  font-size: ${v.typography.metaSize};
  color: ${v.color.textTertiary};
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
`
const MealTags = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: ${v.typography.metaSize};
  color: ${v.color.textTertiary};

  > * {
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  button {
    min-height: 44px;
    padding: 0 8px;
    font-size: ${v.typography.metaSize};
  }
`
/* The balance still owed on a part-paid meal. Success ink, because paying part
   of a hold is progress rather than a fault. */
const LeftToPay = styled.span`
  color: ${v.color.success};
`
const MealsWrap = styled.div`
  display: grid;
  gap: ${v.space.sm};
  margin: 0;
`
const ClosedDayNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin: ${v.space['2xs']} 0;
  border-radius: ${v.radius.md};
  background: ${v.color.bgSurfaceAlt};
  color: ${v.color.textTertiary};
  font-size: ${v.typography.captionSize};
`
const TimetableSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  border-radius: ${v.radius.md};
  background: ${v.color.bgSurface};
  border: 1px solid ${v.color.borderSubtle};

  @media (max-width: 599px) {
    padding: 8px 12px;
    gap: 8px;
  }
`
const TimetableStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`
const TimetableStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`
const StatLabel = styled.span`
  font-size: ${v.typography.metaSize};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${v.color.textTertiary};
  font-weight: 600;
`
const StatValue = styled.span`
  font-size: ${v.typography.bodySize};
  font-weight: 700;
  color: ${v.color.textPrimary};
`
const WeekDayStrip = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 2px 0 6px;
  -webkit-overflow-scrolling: touch;
`
const WeekDayPill = styled.button<{ $hasMeals?: boolean; $isToday?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1 1 0px;
  min-width: 40px;
  padding: 6px 4px;
  border-radius: 10px;
  border: 1.5px solid ${({ $isToday }) => ($isToday ? v.color.brand : v.color.borderSubtle)};
  background: ${({ $isToday }) => ($isToday ? v.color.brandSoft : v.color.bgSurface)};
  cursor: pointer;
  font: inherit;
  transition: border-color 120ms ease, background-color 120ms ease;

  &:hover {
    border-color: ${v.color.brand};
  }
`
const PillDay = styled.span`
  font-size: ${v.typography.metaSize};
  font-weight: 600;
  text-transform: uppercase;
  color: ${v.color.textTertiary};
`
const PillNum = styled.span`
  font-family: ${v.typography.displayFamily};
  font-size: ${v.typography.bodySize};
  font-weight: 700;
  color: ${v.color.textPrimary};
`
const PillDot = styled.span<{ $hasMeals?: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: ${({ $hasMeals }) => ($hasMeals ? v.color.brand : 'transparent')};
  margin-top: 2px;
`
/* ── The unactivated gate ──────────────────────────────────────────────────
   Two faults made this card read as unfinished instead of designed.

   The badge sat in the copy grid, so it stretched to the whole column: a
   full-width bar instead of a label. It now sits in a row that keeps its own
   width.

   The photo set the row height, because it is square, and the copy was much
   shorter. Centring the copy left an empty band above it and another below.
   Now the copy owns the row and the photo fills it, so no dead band can open.

   The card padding comes from the `padding` prop. A second padding on the
   inner slot only doubled it. */
export const MealCalendarPage = () => {
  const navigate = useNavigate()
  const page = usePageContext('/mealplan')
  const { profileId } = useChooseChow()
  const { records: dishes } = useUiDishes()
  const openMap = useChefOpenMap()
  const kitchens = useChefKitchens()
  const orders = useMarketplaceOrderSnapshot() as { records: readonly StoreRecord[] }

  const [weekOffset, setWeekOffset] = useState(0) // 0 = this week, 1 = next week
  const [picker, setPicker] = useState<string | null>(null) // day key being planned
  const [query, setQuery] = useState('')
  const [pickQty, setPickQty] = useState(1)
  /**
   * The dish being reviewed before it is planned. Tapping a row used to place
   * the hold and close the modal in one motion, so the customer never got to
   * look at what they had picked — and a mis-tap in a scrolling list booked a
   * meal. Picking now selects; a second, deliberate action commits.
   */
  const [selected, setSelected] = useState<UiDish | null>(null)
  /** Command in flight, so a double tap cannot plan the same meal twice. */
  const [reserving, setReserving] = useState(false)
  /** What was just planned, kept in the picker so adding several reads as progress. */
  const [added, setAdded] = useState('')
  const [addError, setAddError] = useState('')
  const [pickerSlot, setPickerSlot] = useState<MealSlotKey>('lunch')
  const [showWeekDetails, setShowWeekDetails] = useState(false)
  const selectedHoldIds = useReservationBasketIds(profileId)
  const removingRef = useRef(new Set<string>())
  const [removingIds, setRemovingIds] = useState<readonly string[]>([])
  const [removeErrors, setRemoveErrors] = useState<Record<string, string>>({})


  const myOrders = useMemo(
    () =>
      orders.records.filter((o) =>
        isLiveRuntime() ? String(o.customer_profile_id ?? '') === profileId : true,
      ),
    [orders.records, profileId],
  )

  const orderIds = useMemo(() => {
    const windowKeys = new Set(planWindow().slice(weekOffset * 7, weekOffset * 7 + 7).map(day => day.key))
    return myOrders.filter(order => {
      const status = enumShort(order.status, '')
      const when = new Date(reservationScheduledAt(order.scheduled_at))
      return (status === 'pending_payment' || FIRM_STATUSES.includes(status as typeof FIRM_STATUSES[number])) &&
        Number.isFinite(when.getTime()) && windowKeys.has(dayKey(when))
    }).map(order => String(order.id ?? '')).filter(Boolean)
  }, [myOrders, weekOffset])
  const mealDetails = useOrderItemNamesState(orderIds)
  const orderItemNames = mealDetails.byId

  // Look up a dish by name so a held meal can show the dish's thumbnail —
  // holds only carry the name, but the card wants the same image the menu shows.
  const dishByName = useMemo(() => {
    const m = new Map<string, UiDish>()
    for (const d of dishes) m.set(d.name.toLowerCase(), d)
    return m
  }, [dishes])
  const dishById = useMemo(() => {
    const m = new Map<string, UiDish>()
    for (const d of dishes) m.set(d.id, d)
    return m
  }, [dishes])

  // Offline previews expire holds locally. Live order changes remain server-owned.
  const sweptRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (isLiveRuntime()) return
    const now = Date.now()
    // Use the SAME party-aware cutoff the calendar renders, so a hold shown as
    // "expired" is the hold that gets swept — never one without the other.
    const dayTotal = dayTotalsByKey(myOrders)
    for (const o of myOrders) {
      if (enumShort(o.status, '') !== 'pending_payment') continue
      const raw = reservationScheduledAt(o.scheduled_at)
      if (!raw) continue
      const when = new Date(raw)
      if (Number.isNaN(when.getTime())) continue
      const party = (dayTotal.get(dayKey(when)) ?? 0) >= LARGE_HOLD_MINOR
      if (now <= holdCutoffMs(when, party)) continue // still open — not past its confirm-by
      const id = String(o.id ?? '')
      if (!id || sweptRef.current.has(id)) continue
      sweptRef.current.add(id)
      echoUpsert('marketplace.marketplace_orders', { ...o, status: 'cancelled', status_reason: 'expired' })
      if (isLiveRuntime()) {
        void commands.marketplace
          .setOrderStatus(id, { status: 'cancelled', actor_profile_id: profileId })
          .catch(() => {})
      }
    }
  }, [myOrders, profileId])

  const days = useMemo<DayPlan[]>(() => {
    const now = Date.now()
    // The same window the dish page offers, so a day open in one is open in the
    // other. The calendar adds what is already booked onto each slot.
    const window: DayPlan[] = planWindow().map((slot) => ({
      ...slot,
      held: [],
      firm: [],
      largeHold: false,
      totalMinor: 0,
    }))
    const byKey = new Map(window.map((plan) => [plan.key, plan]))
    for (const o of myOrders) {
      const raw = reservationScheduledAt(o.scheduled_at)
      if (!raw) continue
      const when = new Date(raw)
      if (Number.isNaN(when.getTime())) continue
      const plan = byKey.get(dayKey(when))
      if (!plan) continue
      const status = enumShort(o.status, '')
      const meta = (o.metadata ?? {}) as Record<string, unknown>
      const id = String(o.id ?? '')

      // Robust dish name resolution avoiding generic labels from projections.
      const metaDish = String(meta.dish_name ?? meta.dish_title ?? meta.dishName ?? '').trim()
      const lines = orderItemNames[id]
      const lineItemDish = lines && lines.length > 0 ? lines[0].replace(/\s*×\s*\d+$/, '').trim() : ''
      const summaryName = String(o.item_name ?? meta.item_name ?? o.summary ?? '').trim()
      const dishIds = [
        meta.dish_id,
        meta.item_ref,
        meta.item_id,
        o.item_ref,
        o.item_id,
        o.dish_id,
      ].map((value) => String(value ?? '').trim()).filter(Boolean)
      const matchedById = dishIds.map((dishId) => dishById.get(dishId)).find(Boolean)

      let dishName = [metaDish, lineItemDish, summaryName].find((name) => !isGenericMealName(name)) ?? ''
      if (isGenericMealName(dishName) && matchedById) dishName = matchedById.name
      if (isGenericMealName(dishName)) {
        dishName = mealDetails.pendingIds.includes(id)
          ? 'Loading meal details…' : 'Meal details unavailable'
      }

      const metaChef = String(meta.chef_name ?? meta.kitchen_name ?? '').trim()
      const matchedDish =
        dishByName.get(dishName.toLowerCase()) ||
        matchedById
      const chefName = metaChef || matchedDish?.chef || ''
      const dishImage = String(meta.dish_image ?? '') || matchedDish?.image || ''

      const rawQty = Number(meta.quantity ?? o.quantity ?? 0)
      const lineQty = Number(String(lines?.[0] ?? '').match(/×\s*(\d+)/)?.[1] ?? 0)
      const summaryQty = Number(String(o.summary ?? meta.item_name ?? o.item_name ?? '').match(/×\s*(\d+)/)?.[1] ?? 0)
      const calcQty = matchedDish && matchedDish.price > 0 && Number(o.total_minor ?? o.subtotal_minor ?? 0) > 0
        ? Math.round(Number(o.total_minor ?? o.subtotal_minor) / (matchedDish.price * 100))
        : 0
      const quantity = Math.max(1, rawQty || lineQty || summaryQty || calcQty || 1)

      const meal: Hold = {
        id,
        dishId: String(dishIds[0] ?? matchedDish?.id ?? ''),
        dishName: displayTitle(dishName),
        chefName,
        dishImage,
        quantity,
        totalMinor: Number(o.total_minor ?? o.subtotal_minor ?? 0),
        amountPaidMinor: Number(o.amount_paid_minor ?? 0),
        depositMinor: Number(meta.deposit_minor ?? 0),
        pickup: enumShort(o.delivery_choice) === 'self_pickup',
        status,
        slot: Number(when.toLocaleTimeString('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', hour12: false })) < 11 ? 'breakfast' : Number(when.toLocaleTimeString('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', hour12: false })) >= 16 ? 'dinner' : 'lunch',
      }
      if (status === 'pending_payment') plan.held.push(meal)
      else if (FIRM_STATUSES.includes(status as (typeof FIRM_STATUSES)[number])) plan.firm.push(meal)
    }
    // Party-sized days commit sooner: an earlier confirm-by so a big hold isn't
    // held free until the night before.
    for (const plan of window) {
      plan.totalMinor = [...plan.held, ...plan.firm].reduce((s, m) => s + m.totalMinor, 0)
      if (plan.totalMinor >= LARGE_HOLD_MINOR) {
        plan.largeHold = true
        plan.cutoff = new Date(holdCutoffMs(plan.date, true))
        plan.expired = now > plan.cutoff.getTime()
      }
    }
    return window
  }, [myOrders, dishByName, dishById, orderItemNames, mealDetails.pendingIds])

  /** The seven days on screen. The other week stays loaded so "Copy to next
      week" has somewhere to copy into. */
  const visibleDays = useMemo(
    () => days.slice(weekOffset * 7, weekOffset * 7 + 7),
    [days, weekOffset],
  )
  const [basking, setBasking] = useState(false)
  const [basketNote, setBasketNote] = useState('')

  /** Unpaid reservations remain original orders when added to the cart. */
  const orderableMeals = useMemo<PlannedMeal[]>(
    () =>
      visibleDays.filter((day) => !day.expired).flatMap((day) =>
        day.held.map((meal) => ({
          dish: meal.dishId ? dishById.get(meal.dishId) : undefined,
          dishName: meal.dishName,
          quantity: 1,
          holdId: meal.id,
          balanceMinor: Math.max(0, meal.totalMinor - meal.amountPaidMinor),
        })),
      ),
    [visibleDays, dishById],
  )

  /** Select this week's reservations for one cart payment. */
  const basketThePlan = async (holdIds?: readonly string[]) => {
    if (basking || removingRef.current.size > 0) return
    setBasking(true)
    setBasketNote('')
    try {
      const outcome = await addPlanToCart(holdIds ? orderableMeals.filter((meal) => holdIds.includes(meal.holdId)) : orderableMeals, profileId)
      if (!outcome.ok) {
        setBasketNote(outcome.error)
        return
      }
      navigate('/cart')
    } finally {
      setBasking(false)
    }
  }

  const weeklyStats = useMemo(() => {
    let totalMeals = 0
    let totalMinor = 0
    let activeDays = 0
    for (const d of visibleDays) {
      const count = d.firm.length + d.held.length
      if (count > 0) {
        activeDays++
        totalMeals += count
      }
      totalMinor += d.totalMinor
    }
    return { totalMeals, totalMinor, activeDays }
  }, [visibleDays])
  const weekRange = useMemo(() => {
    if (visibleDays.length === 0) return ''
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    return `${fmt(visibleDays[0].date)} – ${fmt(visibleDays[visibleDays.length - 1].date)}`
  }, [visibleDays])

  const pickable = useMemo(() => {
    const q = query.trim().toLowerCase()
    return dishes.filter((d) => {
      if (d.status !== 'available') return false
      if (!q) return true
      return d.name.toLowerCase().includes(q) || d.chef.toLowerCase().includes(q)
    })
  }, [dishes, query])

  /**
   * Intelligent populate: rank dishes the customer has ordered before (matched
   * by name against the live menu) by how often, so "Suggest my week" fills
   * upcoming open days with what they actually eat. Falls back to available
   * dishes when there's no history yet.
   */
  const suggestions = useMemo<UiDish[]>(() => {
    const counts = new Map<string, number>()
    for (const o of myOrders) {
      const name = String(o.summary ?? o.item_name ?? '').toLowerCase().trim()
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    const available = dishes.filter((d) => d.status === 'available')
    const ranked = available
      .map((d) => ({ d, score: counts.get(d.name.toLowerCase().trim()) ?? 0 }))
      .sort((a, b) => b.score - a.score)
    const fromHistory = ranked.filter((r) => r.score > 0).map((r) => r.d)
    return (fromHistory.length > 0 ? fromHistory : available).slice(0, 5)
  }, [myOrders, dishes])

  const hasHistory = useMemo(
    () => myOrders.some((o) => String(o.summary ?? o.item_name ?? '').trim() !== ''),
    [myOrders],
  )

  const [filling, setFilling] = useState(false)
  const fillWeek = async () => {
    if (suggestions.length === 0 || filling) return
    setFilling(true)
    try {
      const dishForSlot = (slot: MealSlotKey, offset: number) => {
        const matches = suggestions.filter((dish) => slotForDish(dish, offset) === slot)
        return matches[offset % Math.max(1, matches.length)] ?? suggestions[offset % suggestions.length]
      }
      const openDays = visibleDays
        .filter((d) => !d.expired && d.held.length === 0 && d.firm.length === 0)
        .slice(0, 5)
      let i = 0
      for (const day of openDays) {
        for (const slot of ['breakfast', 'lunch', 'dinner'] as const) {
          if (new Date(scheduledAtFor(day.key, slot)).getTime() <= Date.now()) continue
          await reserve(dishForSlot(slot, i), day.key, 1, slot)
          i += 1
        }
      }
    } finally {
      setFilling(false)
    }
  }

  /** The meals already on the day the picker is open for — a day is a day of
   *  meals, so the customer needs to see what they have chosen so far while
   *  they choose the next one. */
  const plannedForPicker = useMemo(() => {
    if (!picker) return []
    const day = visibleDays.find((d) => d.key === picker)
    if (!day) return []
    return [...day.firm, ...day.held]
  }, [picker, visibleDays])

  const plannedSummary = useMemo(() => {
    if (plannedForPicker.length === 0) return ''
    const counts = new Map<string, number>()
    for (const m of plannedForPicker) {
      const name = m.dishName || 'Meal'
      counts.set(name, (counts.get(name) ?? 0) + (m.quantity || 1))
    }
    return [...counts.entries()]
      .map(([name, count]) => (count > 1 ? `${name} (${count} plates)` : name))
      .join(', ')
  }, [plannedForPicker])

  const existingInSlot = useMemo(() => {
    if (!selected || !picker) return 0
    const day = visibleDays.find((d) => d.key === picker)
    if (!day) return 0
    const meals = [...day.held, ...day.firm].filter(
      (m) => m.slot === pickerSlot && m.dishName.toLowerCase() === selected.name.toLowerCase()
    )
    return meals.reduce((sum, m) => sum + (m.quantity || 1), 0)
  }, [selected, picker, pickerSlot, visibleDays])

  /**
   * What to tell the customer about when and where the meal arrives.
   *
   * Three things they cannot infer from a price: the hour it lands (midday, the
   * hour the hold is actually scheduled for), how far off that is in plain
   * words, and which kitchen it travels from. The fourth is the one that can go
   * wrong — a kitchen that does not cook on that weekday, or a dish only
   * offered on certain days — so that is called out rather than discovered on
   * the day.
   */
  const deliveryPlan = useMemo(
    () =>
      selected && picker
        ? planPreview(selected, parseKey(picker), kitchens.get(selected.chefId), pickerSlot)
        : null,
    [selected, picker, kitchens, pickerSlot],
  )

  /**
   * Place one hold and leave the picker open on the day.
   *
   * Two things were wrong here. The optimistic row went through `echoUpsert`,
   * which no-ops in live so that the projection stays authoritative — so in a
   * live deployment nothing at all appeared on the day until a sweep landed,
   * and a `.catch(() => undefined)` on the command made a failed reservation
   * look exactly like a successful one. Live now writes the row only after the
   * command is confirmed (with the id the server gave it, so the projection
   * replaces the same record rather than duplicating it), and a failure says so
   * instead of quietly planning nothing.
   *
   * It also no longer closes the modal. A day is a day of meals, not one meal,
   * so the picker returns to the list with a note about what was just added and
   * remains open until the customer finishes. Payment takes place in the cart.
   */
  const reserve = async (dish: UiDish, key: string, qty = 1, slot: MealSlotKey = pickerSlot) => {
    if (reserving) return

    setReserving(true)
    setAddError('')
    const outcome = await planDish({ dish, dayKey: key, quantity: qty, profileId, mealSlot: slot })
    setReserving(false)
    if (!outcome.ok) {
      setAddError(outcome.error)
      return
    }

    setSelected(null)
    setPickQty(1)
    setQuery('')
    setAdded(qty > 1 ? `${dish.name} (${qty} plates) added to this day.` : `${dish.name} added to this day.`)
  }

  const dropHold = async (hold: Hold) => {
    const idsToDrop = hold.ids && hold.ids.length > 0 ? hold.ids : [hold.id]
    for (const holdId of idsToDrop) {
      if (removingRef.current.has(holdId) || basking) continue
      if (hold.amountPaidMinor > 0 && !window.confirm(
        `Remove this meal? The ${naira(hold.amountPaidMinor)} you already paid will not be refunded.`,
      )) return
      const row = orders.records.find((order) => String(order.id ?? '') === holdId)
      if (!row) continue
      removingRef.current.add(holdId)
      setRemovingIds([...removingRef.current])
      setRemoveErrors((errors) => ({ ...errors, [holdId]: '' }))

      const inCart = selectedHoldIds.includes(holdId)
      // If the meal wasn't added to the cart yet and has no payments, removing it from
      // the meal planner is immediate: we cancel it locally and sync to the server in
      // the background so a 503 or network failure does not trap the meal on the plan.
      if (!inCart && hold.amountPaidMinor === 0) {
        const cancelled: StoreRecord = { ...row, status: 'cancelled', status_reason: 'dropped' }
        confirmedUpsert('marketplace.marketplace_orders', cancelled)
        try {
          removeReservationBasketIds(profileId, [holdId])
        } catch {
          setBasketNote('Meal removed from your plan. If it was in your cart, open your cart again to update it.')
        }
        if (isLiveRuntime()) {
          void commands.marketplace
            .setOrderStatus(holdId, {
              status: 'cancelled', actor_profile_id: profileId, reason: 'dropped',
            })
            .catch(() => undefined)
        }
        removingRef.current.delete(holdId)
        setRemovingIds([...removingRef.current])
        continue
      }

      try {
        let cancelled: StoreRecord = { ...row, status: 'cancelled', status_reason: 'dropped' }
        if (isLiveRuntime()) {
          const response = commandPayload(await commands.marketplace.setOrderStatus(holdId, {
            status: 'cancelled', actor_profile_id: profileId, reason: 'dropped',
          }))
          if (String(response.id ?? '') !== holdId || enumShort(response.status, '') !== 'cancelled') {
            throw new Error('Cancellation was not confirmed.')
          }
          cancelled = { ...cancelled, ...response }
        }
        confirmedUpsert('marketplace.marketplace_orders', cancelled)
        try {
          removeReservationBasketIds(profileId, [holdId])
        } catch {
          setBasketNote('Meal removed from your plan. If it was in your cart, open your cart again to update it.')
        }
      } catch {
        setRemoveErrors((errors) => ({ ...errors, [holdId]: `Could not confirm removal of ${hold.dishName}. Check your connection and try again.` }))
      } finally {
        removingRef.current.delete(holdId)
        setRemovingIds([...removingRef.current])
      }
    }
  }

  /**
   * Repeat weekly (the hidden generator, made explicit): take the meals planned
   * on the nearest upcoming week — keyed by weekday — and materialise them onto
   * the same weekdays of the following week's open days. One tap turns "this
   * week" into "every week" without any recurring backend machinery.
   */
  const [repeating, setRepeating] = useState(false)
  const firstWeek = days.slice(0, 7)
  // Only offer "Repeat weekly" when there's a following week in view to copy the
  // pattern into (PLAN_DAYS > 7). The recurring template otherwise lives on the
  // Meal Plan page; the calendar is a single rolling week.
  const canRepeat = days.length > 7 && firstWeek.some((d) => d.held.length + d.firm.length > 0)
  const repeatWeek = async () => {
    if (repeating) return
    setRepeating(true)
    try {
      const pattern = new Map<number, UiDish>()
      for (const day of firstWeek) {
        const meal = [...day.firm, ...day.held][0]
        if (!meal) continue
        const dish = dishes.find((d) => d.name.toLowerCase() === meal.dishName.toLowerCase())
        const wd = isoWeekday(day.date)
        if (dish && !pattern.has(wd)) pattern.set(wd, dish)
      }
      if (pattern.size === 0) return
      for (const day of days.slice(7)) {
        if (day.expired || day.held.length > 0 || day.firm.length > 0) continue
        const dish = pattern.get(isoWeekday(day.date))
        if (dish) await reserve(dish, day.key)
      }
    } finally {
      setRepeating(false)
    }
  }


  return (
    <Screen header={<PageHeader title={page.title} accentDot />}>
      <PlanHead>
        {mealDetails.failedIds.length > 0 && (
          <MinimalAlert tone="danger" title="Could not load meal details">
            Your saved meals are still in your plan.
            <MinimalButton size="sm" onClick={mealDetails.retry}>Retry meal details</MinimalButton>
          </MinimalAlert>
        )}
        <WeekNav>
          <MinimalButton
            size="sm"
            variant={weekOffset === 0 ? 'primary' : 'secondary'}
            onClick={() => setWeekOffset(0)}
          >
            This week
          </MinimalButton>
          <MinimalButton
            size="sm"
            variant={weekOffset === 1 ? 'primary' : 'secondary'}
            onClick={() => setWeekOffset(1)}
          >
            Next week
          </MinimalButton>
          <WeekRange>{weekRange}</WeekRange>
        </WeekNav>

        <PlanControls>
          <MinimalTooltip content="Choose meals for each day. Add them to your cart and complete payment before the pickup cutoff.">
            <ToolButton variant="quiet" type="button" aria-label="How meal planning works">
              <InfoIcon size="sm" />
            </ToolButton>
          </MinimalTooltip>

          {suggestions.length > 0 && (
            <MinimalTooltip
              content={
                hasHistory
                  ? 'Fill empty days with meals you order often.'
                  : 'Fill empty days with meals people order most.'
              }
            >
              <ToolButton variant="quiet"
                type="button"
                onClick={fillWeek}
                disabled={filling}
                aria-label="Fill my empty days"
              >
                <SparkIcon size="sm" /> Suggest meals
              </ToolButton>
            </MinimalTooltip>
          )}

          {canRepeat && (
            <MinimalTooltip content="Copy this week's meals to next week.">
              <ToolButton variant="quiet"
                type="button"
                onClick={repeatWeek}
                disabled={repeating}
                aria-label="Copy this week to next week"
              >
                <CopyIcon size="sm" /> Repeat week
              </ToolButton>
            </MinimalTooltip>
          )}


        </PlanControls>
      </PlanHead>

      <PlanBody>
        <TimetableSummary>
          {showWeekDetails ? (
            <TimetableStats>
              <TimetableStatItem>
                <StatLabel>Meals Planned</StatLabel>
                <StatValue>{weeklyStats.totalMeals} {weeklyStats.totalMeals === 1 ? 'meal' : 'meals'}</StatValue>
              </TimetableStatItem>
              <TimetableStatItem>
                <StatLabel>Active Days</StatLabel>
                <StatValue>{weeklyStats.activeDays} of 7 days</StatValue>
              </TimetableStatItem>
              <TimetableStatItem>
                <StatLabel>Week Budget</StatLabel>
                <StatValue>{weeklyStats.totalMinor > 0 ? naira(weeklyStats.totalMinor) : '₦0'}</StatValue>
              </TimetableStatItem>
            </TimetableStats>
          ) : (
            <TimetableStats>
              <TimetableStatItem>
                <StatLabel>Meals Planned</StatLabel>
                <StatValue>{weeklyStats.totalMeals} {weeklyStats.totalMeals === 1 ? 'meal' : 'meals'}</StatValue>
              </TimetableStatItem>
              <TimetableStatItem>
                <StatLabel>Week Budget</StatLabel>
                <StatValue>{weeklyStats.totalMinor > 0 ? naira(weeklyStats.totalMinor) : '₦0'}</StatValue>
              </TimetableStatItem>
            </TimetableStats>
          )}
          <PaymentNote>
            Plan meals for pickup. Each meal has its own service fee. Add meals to your cart and pay before the pickup cutoff.
          </PaymentNote>
          {orderableMeals.length > 0 && (
            <MinimalButton
              size="sm"
              loading={basking}
              disabled={basking || removingIds.length > 0}
              onClick={() => void basketThePlan()}
            >
              <CardIcon size="sm" /> Add week to cart · {naira(orderableMeals.reduce((sum, meal) => sum + (meal.balanceMinor ?? 0), 0))}
            </MinimalButton>
          )}
          {basketNote && <BasketNote role="status">{basketNote}</BasketNote>}
          <SummaryToggle variant="quiet"
            type="button"
            aria-expanded={showWeekDetails}
            onClick={() => setShowWeekDetails((value) => !value)}
          >
            <InfoIcon size="sm" />
            {showWeekDetails ? 'Hide overview' : 'Show overview'}
          </SummaryToggle>
        </TimetableSummary>

        {showWeekDetails && (
          <WeekDayStrip aria-label="Day timetable selector">
            {visibleDays.map((d) => {
              const hasMeals = d.held.length + d.firm.length > 0
              const isToday = d.weekday === 'Today'
              return (
                <WeekDayPill
                  key={d.key}
                  type="button"
                  $hasMeals={hasMeals}
                  $isToday={isToday}
                  onClick={() => {
                    const el = document.getElementById(`day-${d.key}`)
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                  }}
                >
                  <PillDay>{d.weekday.slice(0, 3)}</PillDay>
                  <PillNum>{d.date.getDate()}</PillNum>
                  <PillDot $hasMeals={hasMeals} />
                </WeekDayPill>
              )
            })}
          </WeekDayStrip>
        )}

        <Grid>
        {visibleDays.map((day) => {
          const isEmpty = day.held.length === 0 && day.firm.length === 0
          const isToday = day.weekday === 'Today'
          const mealCount = day.firm.length + day.held.length
          const mealsBySlot = new Map<MealSlotKey, { meal: Hold; firm: boolean }[]>()
          for (const entry of [...day.firm.map((meal) => ({ meal, firm: true })), ...day.held.map((meal) => ({ meal, firm: false }))]) {
            const list = mealsBySlot.get(entry.meal.slot) ?? []
            const existing = list.find((item) => item.firm === entry.firm && item.meal.dishName.toLowerCase() === entry.meal.dishName.toLowerCase())
            if (existing && !entry.firm) {
              existing.meal = {
                ...existing.meal,
                ids: [...(existing.meal.ids ?? [existing.meal.id]), entry.meal.id],
                quantity: (existing.meal.quantity || 1) + (entry.meal.quantity || 1),
                totalMinor: existing.meal.totalMinor + entry.meal.totalMinor,
                amountPaidMinor: existing.meal.amountPaidMinor + entry.meal.amountPaidMinor,
                depositMinor: existing.meal.depositMinor + entry.meal.depositMinor,
              }
            } else {
              list.push(entry)
            }
            mealsBySlot.set(entry.meal.slot, list)
          }

          const renderMeal = (m: Hold, firm: boolean) => {
            const dish = dishByName.get(m.dishName.toLowerCase())
            const tone = day.expired ? 'neutral' : firm ? 'brand' : m.amountPaidMinor > 0 ? 'info' : 'warning'
            const isRemoving = (m.ids ?? [m.id]).some((id) => removingIds.includes(id))
            const label = day.expired ? 'Cancelled' : firm ? 'Paid' : selectedHoldIds.includes(m.id) ? 'In cart' : 'Planned'
            const chef = m.chefName || dish?.chef
            const thumb = m.dishImage || dish?.image
            const hasError = (m.ids ?? [m.id]).map((id) => removeErrors[id]).find(Boolean)

            return (
              <MealCard key={m.id} $muted={day.expired}>
                <MealThumb>
                  <ChowThumb
                    src={thumb}
                    emoji={dish?.glyph ?? '🍽️'}
                    alt={m.dishName}
                    name={m.dishName}
                    category={dish?.category}
                  />
                </MealThumb>
                <MealMain>
                  <MealTop>
                    <MealName>
                      {m.dishName}
                      {m.quantity > 1 && (
                        <span style={{ fontSize: '0.85em', opacity: 0.8, marginLeft: 6, fontWeight: 500 }}>
                          ({m.quantity} plates)
                        </span>
                      )}
                    </MealName>
                    <Money amount={m.totalMinor / 100} size="sm" tone={firm ? 'brand' : 'default'} />
                  </MealTop>
                  {chef && <MealChef>by {chef}</MealChef>}
                  <MealTags>
                    {m.pickup && <MealChef>Kitchen pickup</MealChef>}
                    {m.quantity > 1 && (
                      <MinimalBadge tone="neutral" emphasis="soft" size="sm">
                        {m.quantity} plates
                      </MinimalBadge>
                    )}
                    <MinimalBadge tone={tone} emphasis="soft" size="sm">{label}</MinimalBadge>
                    {!firm && m.amountPaidMinor > 0 && (
                      <LeftToPay>{naira(m.totalMinor - m.amountPaidMinor)} left to pay</LeftToPay>
                    )}
                    {!firm && !day.expired && (
                      <MinimalButton size="sm" variant="ghost" disabled={isRemoving || basking} onClick={() => void dropHold(m)}>
                        {isRemoving ? 'Removing…' : 'Remove from plan'}
                      </MinimalButton>
                    )}
                  </MealTags>
                  {hasError && <BasketNote role="alert">{hasError}</BasketNote>}
                </MealMain>
              </MealCard>
            )
          }

          return (
            <DayCard id={`day-${day.key}`} key={day.key} padding="sm" enter={false}>
              <DayTop>
                <DateTile $today={isToday}>
                  <DateNum>{day.date.getDate()}</DateNum>
                  <DateMon>{day.date.toLocaleDateString(undefined, { month: 'short' })}</DateMon>
                </DateTile>
                <DayHeading>
                  <DayTitleRow>
                    <DayTitle>{day.weekday}</DayTitle>
                    {day.expired ? (
                      <MinimalBadge tone="neutral" emphasis="soft" size="sm">Closed</MinimalBadge>
                    ) : day.largeHold ? (
                      <MinimalBadge tone="warning" emphasis="soft" size="sm">Big order</MinimalBadge>
                    ) : mealCount > 0 ? (
                      <MinimalBadge tone="brand" emphasis="soft" size="sm">
                        {mealCount === 1 ? '1 meal' : `${mealCount} meals`}
                      </MinimalBadge>
                    ) : null}
                  </DayTitleRow>
                  <DaySub>{day.label}</DaySub>
                </DayHeading>
              </DayTop>

              {day.largeHold && !day.expired && (
                <BigOrderNote>
                  Big order — pay for this day by {day.cutoff.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} so the kitchen can shop for it.
                </BigOrderNote>
              )}

              {isEmpty && day.expired ? (
                <ClosedDayNotice>Ordering closed for this day.</ClosedDayNotice>
              ) : (
                <MealsWrap>
                  {MEAL_SLOTS.map(({ key, label, short, Icon }) => {
                    const entries = mealsBySlot.get(key) ?? []
                    return (
                      <MealSlot key={key}>
                        <MealSlotLabel>
                          <Icon size="sm" />
                          <span>{short}</span>
                        </MealSlotLabel>
                        <div style={{ display: 'grid', gap: 6 }}>
                          {entries.map((entry) => renderMeal(entry.meal, entry.firm))}
                          {!day.expired && new Date(scheduledAtFor(day.key, key)).getTime() > Date.now() ? (
                            <EmptyMealSlot variant="quiet"
                              type="button"
                              aria-label={`Add ${entries.length ? 'a dish or drink' : 'a meal'} for ${day.weekday} ${label}`}
                              onClick={() => { setPickerSlot(key); setPicker(day.key) }}
                            >
                              <PlusIcon size="sm" /> {entries.length ? 'Add dish or drink' : 'Add'}
                            </EmptyMealSlot>
                          ) : entries.length === 0 ? <ClosedDayNotice>{day.expired ? 'No meal planned.' : 'Meal time has passed.'}</ClosedDayNotice> : null}
                        </div>
                      </MealSlot>
                    )
                  })}
                </MealsWrap>
              )}

              {mealCount > 0 && (
                <DayFooter>
                  {!day.expired && (
                    <MinimalButton size="sm" variant="secondary" onClick={() => setPicker(day.key)}>
                      <PlusIcon size="sm" /> Add another meal
                    </MinimalButton>
                  )}
                  {day.held.length > 0 && !day.expired && (
                    <MinimalButton size="sm" disabled={basking || removingIds.length > 0} onClick={() => void basketThePlan(day.held.map((meal) => meal.id))}>
                      <CardIcon size="sm" /> Add day to cart
                    </MinimalButton>
                  )}
                </DayFooter>
              )}
            </DayCard>
          )
        })}
        </Grid>
      </PlanBody>

      {/* Dish picker for the chosen day */}
      <ChowModal
        open={picker !== null}
        title={picker ? `Plan meals · ${parseKey(picker).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}` : 'Plan meals'}
        onClose={() => {
          setPicker(null)
          setQuery('')
          setPickQty(1)
          setSelected(null)
          setAdded('')
          setAddError('')
        }}
      >
        <div role="group" aria-label="Meal time" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {MEAL_SLOTS.map(({ key, label }) => (
            <MinimalButton key={key} size="sm" variant={pickerSlot === key ? 'primary' : 'secondary'} disabled={Boolean(picker && new Date(scheduledAtFor(picker, key)).getTime() <= Date.now())} aria-pressed={pickerSlot === key} onClick={() => setPickerSlot(key)}>
              {label}
            </MinimalButton>
          ))}
        </div>
        {addError && (
          <MinimalAlert tone="danger" title="Could not plan that meal">
            {addError}
          </MinimalAlert>
        )}
        {plannedSummary && (
          <PlannedSoFar>
            <strong>
              {plannedForPicker.length === 1 ? '1 meal' : `${plannedForPicker.length} meals`} on this day:
            </strong>{' '}
            {plannedSummary}
          </PlannedSoFar>
        )}
        {selected && deliveryPlan ? (
          /* Step two: what you picked, when it arrives, and only then the
             button that books it. */
          <Review>
            <ReviewTop>
              <ReviewThumb>
                <ChowThumb
                  src={selected.image}
                  emoji={selected.glyph}
                  alt={selected.name}
                  name={selected.name}
                  category={selected.category}
                />
              </ReviewThumb>
              <ReviewTitle>
                <ReviewName>{selected.name}</ReviewName>
                <DaySub>by {selected.chef}</DaySub>
                {selected.description && <DaySub>{selected.description}</DaySub>}
              </ReviewTitle>
            </ReviewTop>

            <DeliveryNote>
              <span>
                <strong>{deliveryPlan.when}</strong> · {deliveryPlan.time}
              </span>
              <span>
                {deliveryPlan.from}
                {deliveryPlan.prep && ` · ${deliveryPlan.prep}`}
              </span>
              {deliveryPlan.caution && <DeliveryWarn>{deliveryPlan.caution}</DeliveryWarn>}
              {depositFor(Math.round(selected.price * 100) * pickQty) > 0 && (
                <span>
                  Large orders need payment before the kitchen starts preparation. Review your meals in the cart.
                </span>
              )}
            </DeliveryNote>

            {existingInSlot > 0 && (
              <SlotHint>
                Already have {existingInSlot} {existingInSlot === 1 ? 'plate' : 'plates'} planned for {pickerSlot}.
                Adding {pickQty} more will make it {existingInSlot + pickQty} plates.
              </SlotHint>
            )}

            <QtyRow>
              <span>Plates</span>
              <Stepper value={pickQty} onChange={setPickQty} min={1} max={200} />
              <Money amount={selected.price * pickQty} tone="brand" size="sm" />
            </QtyRow>

            <ReviewActions>
              <MinimalButton size="sm" variant="ghost" onClick={() => setSelected(null)}>
                Choose another
              </MinimalButton>
              <MinimalButton
                size="sm"
                loading={reserving}
                disabled={reserving}
                onClick={() => picker && reserve(selected, picker, pickQty)}
              >
                Plan this meal
              </MinimalButton>
            </ReviewActions>
          </Review>
        ) : (
          <>
            {added && (
              <AddedNote role="status">
                <AddedTick aria-hidden>✓</AddedTick>
                <span>{added} Add another, or close when you are done.</span>
              </AddedNote>
            )}
            <PickerSearch>
              <SearchHint>Search dishes or chefs to plan meals for this day:</SearchHint>
              <SearchField placeholder="Search dishes or chefs" value={query} onChange={setQuery} />
            </PickerSearch>
            {pickable.length === 0 ? (
              <MinimalEmptyState icon={<PlateIcon />} title="No meals match" description="Try another name." />
            ) : (
              <PickList>
                {pickable.slice(0, 40).map((dish) => {
                  const chefOpen = openMap.get(dish.chefId) !== false
                  const dep = depositFor(Math.round(dish.price * 100))
                  const inSlotCount = plannedForPicker
                    .filter((m) => m.slot === pickerSlot && m.dishName.toLowerCase() === dish.name.toLowerCase())
                    .reduce((sum, m) => sum + (m.quantity || 1), 0)
                  return (
                    <PickRow
                      key={dish.id}
                      type="button"
                      onClick={() => {
                        setSelected(dish)
                        setAdded('')
                        setAddError('')
                      }}
                    >
                      <PickThumb>
                        <ChowThumb src={dish.image} emoji={dish.glyph} alt={dish.name} name={dish.name} category={dish.category} />
                      </PickThumb>
                      <PickMeta>
                        <MealName>
                          {dish.name}
                          {inSlotCount > 0 && (
                            <span style={{ fontSize: '0.82em', opacity: 0.8, marginLeft: 6, fontWeight: 500 }}>
                              ({inSlotCount} in {pickerSlot})
                            </span>
                          )}
                        </MealName>
                        <DaySub>
                          by {dish.chef}
                          {!chefOpen && ' · closed now, can still cook this day'}
                          {dep > 0 && ` · early confirmation required`}
                        </DaySub>
                      </PickMeta>
                      <Money amount={dish.price} tone="brand" size="sm" />
                    </PickRow>
                  )
                })}
              </PickList>
            )}
          </>
        )}
      </ChowModal>


    </Screen>
  )
}
